import React, { useEffect, useState } from 'react';
import { PrismicImage } from '@prismicio/react';
import { useLocation } from '@reach/router';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import Colors from '@components/shared/Colors';
import { TEXT_STYLES, TextVariant } from '@components/shared/design/Typography';
import LinkTranslated from '@components/shared/LinkTranslated';
import useDesktop from '@components/shared/useDesktop';
import { Article, CustomArticleData, ArticleData } from '@contracts';
import { useCountry } from '@hooks/useCountry';
import { getCategoryColour, getCategoryTitle } from '@prismicVitals';
import { replaceText } from '@utils/handleTextReplacement';

interface Props {
  article: Article | Article<CustomArticleData>;
  onlyMobileDesign?: boolean;
  callback?: () => void;
}

const ArticleItemCarousel: React.FC<Props> = ({ article, callback = () => undefined, onlyMobileDesign }) => {
  const { featured_image, article_subheadline, title, category } = article.data as ArticleData;
  const { custom_url } = article.data as CustomArticleData;
  const breakpoint = onlyMobileDesign ? Number.MAX_SAFE_INTEGER : 800;
  const desktop = useDesktop(breakpoint);
  const { country } = useCountry();
  const location = useLocation();
  const [categoryTitle, setCategoryTitle] = useState('');
  const [categoryColor, setCategoryColor] = useState('');
  const imageSize = desktop ? 340 : 300;
  const { t } = useTranslation();

  useEffect(() => {
    if (category) {
      getCategoryTitle(setCategoryTitle, category.id);
      getCategoryColour(setCategoryColor, category);
    }
  }, [category]);

  return (
    <Container $breakpoint={breakpoint} className="article-container" data-testid="article-item">
      <InnerContainer $breakpoint={breakpoint}>
        <ImageWrapper $breakpoint={breakpoint}>
          <StyledLink
            to={custom_url ?? `/vitals/${category.uid}/${article.uid}/`}
            aria-label={`Read ${title}`}
            target={custom_url ? '_blank' : ''}
            onClick={() => callback()}
          >
            <PrismicImage field={featured_image} imgixParams={{ width: imageSize, height: imageSize }} alt={title} loading="lazy" />
          </StyledLink>
        </ImageWrapper>
        <TextWrapper $breakpoint={breakpoint}>
          <CategoryName $bgColor={categoryColor || Colors.sky}>{categoryTitle}</CategoryName>
          <ArticleTitle>
            <StyledLink
              to={custom_url ?? `/vitals/${category.uid}/${article.uid}/`}
              aria-label={`Read ${title}`}
              target={custom_url ? '_blank' : ''}
              onClick={() => callback()}
            >
              {replaceText(title, country, location)}
            </StyledLink>
          </ArticleTitle>
          <Description $breakpoint={breakpoint}>{article_subheadline}</Description>
          <ReadNow
            to={custom_url ?? `/vitals/${category.uid}/${article.uid}/`}
            aria-label={`Read ${title}`}
            target={custom_url ? '_blank' : ''}
            onClick={() => callback()}
          >
            {t('components.read-now')}
          </ReadNow>
        </TextWrapper>
      </InnerContainer>
    </Container>
  );
};

export default ArticleItemCarousel;

const Container = styled.div<{ $breakpoint: number }>`
  display: flex;
  margin-right: 32px;
  justify-content: flex-start;
  width: 560px;
  min-width: 560px;

  @media (max-width: ${(props) => props.$breakpoint}px) {
    width: 240px;
    min-width: 240px;
    margin-right: 24px;
  }
`;

const InnerContainer = styled.div<{ $breakpoint: number }>`
  display: flex;
  flex-direction: row;
  column-gap: 24px;
  max-width: 560px;

  @media (max-width: ${(props) => props.$breakpoint}px) {
    flex-direction: column;
    row-gap: 16px;
  }
`;

const ImageWrapper = styled.div<{ $breakpoint: number }>`
  aspect-ratio: 1/1;
  width: 280px;
  height: 280px;

  a {
    display: block;
  }

  img {
    border: 2px solid ${Colors.cream};
    border-radius: 12px;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: ${(props) => props.$breakpoint}px) {
    width: 100%;
    min-width: 100%;
    height: auto;
  }
`;

const TextWrapper = styled.div<{ $breakpoint: number }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  row-gap: 16px;

  @media (max-width: ${(props) => props.$breakpoint}px) {
    row-gap: 8px;
  }
`;

const CategoryName = styled.h6<{ $bgColor: string }>`
  ${TEXT_STYLES[TextVariant.UpperText5]};
  background-color: ${(props) => props.$bgColor};
  padding: 5px 5px 3px 7px;
  text-align: left;
  color: ${Colors.navyBlue};
  text-transform: uppercase;
  border-radius: 4px;

  &:empty {
    display: none;
  }
`;

const ArticleTitle = styled.h3`
  ${TEXT_STYLES[TextVariant.Heading7]};
  text-align: left;

  a {
    color: ${Colors.navyBlue};
  }

  &:hover a {
    color: ${Colors.lilac};
  }
`;

const Description = styled.p<{ $breakpoint: number }>`
  ${TEXT_STYLES[TextVariant.BodyRegularLG]};
  text-align: left;
  color: ${Colors.navyBlue};

  @media (max-width: ${(props) => props.$breakpoint}px) {
    display: none;
  }
`;

const StyledLink = styled(LinkTranslated)`
  text-decoration: none;

  &:hover {
    text-decoration: none;
  }
`;

const ReadNow = styled(LinkTranslated)`
  ${TEXT_STYLES[TextVariant.BodyBoldLG]};
  color: ${Colors.navyBlue};
  text-decoration-line: underline;
  text-underline-offset: 4px;
  text-decoration-thickness: 3px;
`;
